import React, { Fragment } from 'react';
import 'moment-timezone';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import Separator from 'widgets-v5/separator';
/** import widgets components in the section above, Sort Lines Ascending */

import {
    checkIsNewScheduledWeightedRotationMode,
    CreativeRotationMethodMapping,
} from 'states/resources/ads/business-logic';
import {
    CREATIVE_HISTORY_CELL_STYLE,
    CREATIVE_HISTORY_ID_COLUMN_STYLE,
    CREATIVE_HISTORY_NAME_CELL_STYLE,
    CREATIVE_HISTORY_TABLE_STYLE,
    CREATIVE_HISTORY_WEIGHT_COLUMN_STYLE,
} from './creative-history-styles';
import { formatDate_long, formatNumber_percentage } from 'utils/formatting';
/** import multiple application imports in the section above, Sort Lines Ascending */

import { getWeatherConditionFieldLabels } from 'states/resources/weather-conditions/selectors';
/** import single application imports in the section above, Sort Lines Ascending */

const SCHEDULE_WINDOW_STYLE = { marginBottom: 16 };

/**
 * Live creative name, or the markup id when the creative is missing.
 * The id is never prefixed with #.
 *
 * @param {Object<string, {attr?: {name?: string}}>} creatives - Live creatives keyed by markup id.
 * @param {string|number} markupId - Creative markup id.
 * @returns {string|number} Creative name or markup id.
 */
export function creativeHistoryName(creatives, markupId) {
    return creatives?.[markupId]?.attr?.name ?? markupId;
}

/**
 * Column label. Default Typography renders h6/p, which word-highlight ignores.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Label text.
 * @returns {JSX.Element}
 */
function CreativeHistoryLabel({ children }) {
    return <Typography variant="subtitle2">{children}</Typography>;
}

/**
 * Compared value. Typography carries body2 styles on a classed div.
 * The inner div has no attributes so leafDivs / highlightHtmlWords can diff it alone.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Plain value text.
 * @returns {JSX.Element}
 */
function CreativeHistoryValue({ children }) {
    return (
        <Typography variant="body2" component="div">
            <div>{children}</div>
        </Typography>
    );
}

/**
 * Schedule window title. The date range is the leaf div; the subtitle1 wrapper is classed.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Formatted date range.
 * @returns {JSX.Element}
 */
function CreativeHistoryHeading({ children }) {
    return (
        <Typography variant="subtitle1" component="div" gutterBottom>
            <div>{children}</div>
        </Typography>
    );
}

/**
 * ID or Weight cell. Text stays on one line so those columns do not wrap.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Column label or value.
 * @returns {JSX.Element}
 */
function HistoryCell({ children }) {
    return <td style={CREATIVE_HISTORY_CELL_STYLE}>{children}</td>;
}

/** Name header and value cell. Wraps long names inside the Name column. */
function NameHistoryCell({ children }) {
    return <td style={CREATIVE_HISTORY_NAME_CELL_STYLE}>{children}</td>;
}

/**
 * Shared creative table. Header is ID, Name, and Weight only when a row has a weight.
 * One body row per creative. An empty list is a bare leaf div, None, with no header table.
 *
 * @param {Array<{key?: string|number, markupId: string|number, name: string|number, weight?: string}>} rows
 * @returns {JSX.Element}
 */
export function renderCreativeHistoryTable(rows = []) {
    if (rows.length === 0) {
        return <div>None</div>;
    }

    const showWeight = rows.some(row => row.weight !== undefined && row.weight !== null);

    return (
        <table style={CREATIVE_HISTORY_TABLE_STYLE}>
            <colgroup>
                <col style={CREATIVE_HISTORY_ID_COLUMN_STYLE} />
                <col />
                {showWeight && <col style={CREATIVE_HISTORY_WEIGHT_COLUMN_STYLE} />}
            </colgroup>
            <tbody>
                <tr>
                    <HistoryCell>
                        <CreativeHistoryLabel>ID</CreativeHistoryLabel>
                    </HistoryCell>
                    <NameHistoryCell>
                        <CreativeHistoryLabel>Name</CreativeHistoryLabel>
                    </NameHistoryCell>
                    {showWeight && (
                        <HistoryCell>
                            <CreativeHistoryLabel>Weight</CreativeHistoryLabel>
                        </HistoryCell>
                    )}
                </tr>
                {rows.map(row => (
                    <tr key={row.key ?? row.markupId}>
                        <HistoryCell>
                            <CreativeHistoryValue>{row.markupId}</CreativeHistoryValue>
                        </HistoryCell>
                        <NameHistoryCell>
                            <CreativeHistoryValue>{row.name}</CreativeHistoryValue>
                        </NameHistoryCell>
                        {showWeight && (
                            <HistoryCell>
                                {row.weight !== undefined && row.weight !== null && (
                                    <CreativeHistoryValue>{row.weight}</CreativeHistoryValue>
                                )}
                            </HistoryCell>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/**
 * Weighted, Even, or optimized creative list.
 * Weighted and Even pass includeWeight so rule.weight is formatted as a fraction.
 * Optimized modes omit Weight even when the rule still has a weight value.
 *
 * @param {Object} props
 * @param {Object} props.creatives - Live creatives keyed by markup id.
 * @param {Array<{markup_id: string|number, weight?: number}>} [props.rules]
 * @param {boolean} props.includeWeight
 * @returns {JSX.Element}
 */
export function renderRuleCreativeHistory({ creatives, rules, includeWeight }) {
    const list = Array.isArray(rules) ? rules : [];

    return renderCreativeHistoryTable(
        list.map(rule => {
            const row = {
                key: rule.markup_id,
                markupId: rule.markup_id,
                name: creativeHistoryName(creatives, rule.markup_id),
            };

            if (includeWeight) {
                row.weight = formatNumber_percentage(rule.weight);
            }

            return row;
        })
    );
}

/**
 * Single Creative history. No Weight column.
 *
 * @param {Object} props
 * @param {Object} props.creatives - Live creatives keyed by markup id.
 * @param {Array<string|number>} [props.creativeIds]
 * @returns {JSX.Element}
 */
export function renderSingleCreativeHistory({ creatives, creativeIds }) {
    const ids = Array.isArray(creativeIds) ? creativeIds : [];

    return renderCreativeHistoryTable(
        ids.map(markupId => ({
            key: markupId,
            markupId,
            name: creativeHistoryName(creatives, markupId),
        }))
    );
}

/**
 * Renders one schedule window: date-range separator, then the shared creative table or None.
 * Window weights are stored as 0-100, so they are divided by 100 before percentage formatting.
 * Legacy windows (no scheduled.0.id) are ID and Name only.
 *
 * @param {Object} props - Window props.
 * @param {Object} props.schedule - Window with start, end, and optional weighted rows.
 * @param {Object} props.creatives - Live creatives keyed by markup id.
 * @param {string} props.timezone - Timezone used to format the window dates.
 * @param {Object} props.rotationRules - Ad rotation rules, including the schedule mode.
 * @returns {JSX.Element} The window date range and its creative table.
 */
export function renderScheduleWindow({ schedule, creatives, timezone, rotationRules }) {
    const dateRange = `${formatDate_long(schedule.start, timezone)} - ${formatDate_long(
        schedule.end,
        timezone
    )}`;
    const rows = checkIsNewScheduledWeightedRotationMode(rotationRules)
        ? (schedule.weighted ?? []).map(weighted => ({
              key: weighted.id,
              markupId: weighted.markup_id,
              name: creativeHistoryName(creatives, weighted.markup_id),
              weight: formatNumber_percentage(weighted.weight / 100),
          }))
        : schedule.markup_id === undefined || schedule.markup_id === null
        ? []
        : [
              {
                  key: schedule.markup_id,
                  markupId: schedule.markup_id,
                  name: creativeHistoryName(creatives, schedule.markup_id),
              },
          ];

    return (
        <div key={schedule.id || schedule.markup_id} style={SCHEDULE_WINDOW_STYLE}>
            <Separator label={<CreativeHistoryHeading>{dateRange}</CreativeHistoryHeading>} />
            {renderCreativeHistoryTable(rows)}
        </div>
    );
}

/**
 * Renders scheduled history as the schedule windows only.
 *
 * @param {Object} props - History props.
 * @param {Object} props.rotationRules - Ad rotation rules, including `scheduled` windows.
 * @param {Object} props.creatives - Live creatives keyed by markup id.
 * @param {string} props.timezone - Timezone used to format window dates.
 * @returns {JSX.Element} Schedule windows.
 */
export function renderScheduledCreativeHistory({ rotationRules, creatives, timezone }) {
    const scheduled = rotationRules?.scheduled ?? [];

    return (
        <Fragment>
            {scheduled.map(schedule =>
                renderScheduleWindow({
                    schedule,
                    creatives,
                    timezone,
                    rotationRules,
                })
            )}
        </Fragment>
    );
}

/**
 * One creative row as ID / Name / Weight lines, matching Custom Field's newline clipboard text.
 * Weather rows also include Condition, Qualifier, Value, and Time Frame.
 *
 * @param {Object} row
 * @returns {string}
 */
function formatCreativeClipboardRow(row) {
    const lines = [`ID: ${row.markupId}`, `Name: ${row.name}`];

    if (row.weight !== undefined && row.weight !== null) {
        lines.push(`Weight: ${row.weight}`);
    }
    if (row.conditionLabel !== undefined) {
        lines.push(`Condition: ${row.conditionLabel}`);
        lines.push(`Qualifier: ${row.qualifierLabel}`);
        lines.push(`Value: ${row.valueLabel}`);
        lines.push(`Time Frame: ${row.timeframeLabel}`);
    }

    return lines.join('\n');
}

/**
 * Plain-text creative history for the copy button.
 * Groups are separated by a blank line. An empty group is the word None.
 * Line breaks match Custom Field clipboard text, which joins lines with `\n`.
 *
 * @param {Object} props
 * @param {string} props.rotationMethodLabel
 * @param {Array<{title?: string, rows?: Array}>} [props.groups]
 * @returns {string}
 */
export function formatCreativeHistoryClipboardText({ rotationMethodLabel, groups = [] }) {
    const sections = groups.map(group => {
        const body =
            group.rows && group.rows.length
                ? group.rows.map(formatCreativeClipboardRow).join('\n\n')
                : 'None';

        return group.title ? `${group.title}\n${body}` : body;
    });

    return [`Rotation Method: ${rotationMethodLabel}`, ...sections].join('\n\n');
}

/** Optimized rotation modes. Weight is not shown for these, even when the rule still has one. */
const OPTIMIZED_ROTATION_MODES = [
    CreativeRotationMethodMapping.OptimizedForClicks,
    CreativeRotationMethodMapping.OptimizedForConversions,
    CreativeRotationMethodMapping.OptimizedForVideoCompletion,
];

/**
 * One schedule window for the creative clipboard.
 * The title is the date range. An empty window is a group with no rows, which copies as None.
 * Weighted windows include Weight. A legacy window is ID and Name only.
 *
 * @param {Object} schedule - Window with start, end, and optional weighted rows.
 * @param {Object} creatives - Live creatives keyed by markup id.
 * @param {string} timezone - Timezone used to format the date range.
 * @param {Object} rotationRules - Ad rotation rules, including the schedule mode.
 * @returns {{ title: string, rows: Array }}
 */
function scheduleClipboardGroup(schedule, creatives, timezone, rotationRules) {
    const dateRange = `${formatDate_long(schedule.start, timezone)} - ${formatDate_long(
        schedule.end,
        timezone
    )}`;
    const rows = checkIsNewScheduledWeightedRotationMode(rotationRules)
        ? (schedule.weighted ?? []).map(weighted => ({
              markupId: weighted.markup_id,
              name: creativeHistoryName(creatives, weighted.markup_id),
              weight: formatNumber_percentage(weighted.weight / 100),
          }))
        : schedule.markup_id === undefined || schedule.markup_id === null
        ? []
        : [
              {
                  markupId: schedule.markup_id,
                  name: creativeHistoryName(creatives, schedule.markup_id),
              },
          ];

    return { title: dateRange, rows };
}

/**
 * Clipboard groups for one ad snapshot, matching the creative history tables.
 *
 * @param {Object} props
 * @param {Object} props.snapshot
 * @param {Object} props.creatives
 * @param {Object} [props.weatherConditionsData]
 * @returns {Array<{title?: string, rows: Array}>}
 */
export function creativeHistoryClipboardGroups({ snapshot, creatives, weatherConditionsData }) {
    const mode = snapshot?.rotation_rules?.mode ?? CreativeRotationMethodMapping.Single;
    const rotationRules = snapshot?.rotation_rules;
    const timezone = snapshot?.timezone;
    const groups = [];

    if (
        [
            CreativeRotationMethodMapping.Weighted,
            CreativeRotationMethodMapping.Even,
            ...OPTIMIZED_ROTATION_MODES,
        ].includes(mode)
    ) {
        const includeWeight = !OPTIMIZED_ROTATION_MODES.includes(mode);
        const rules = rotationRules?.weighted ?? [];
        groups.push({
            rows: rules.map(rule => {
                const row = {
                    markupId: rule.markup_id,
                    name: creativeHistoryName(creatives, rule.markup_id),
                };
                if (includeWeight) {
                    row.weight = formatNumber_percentage(rule.weight);
                }
                return row;
            }),
        });
        (rotationRules?.scheduled ?? []).forEach(schedule => {
            groups.push(scheduleClipboardGroup(schedule, creatives, timezone, rotationRules));
        });
        return groups;
    }

    if (mode === CreativeRotationMethodMapping.Weather) {
        const settings = rotationRules?.weather ?? [];
        const nonDefault = settings.filter(setting => !setting.isDefault);
        const defaults = settings.filter(setting => setting.isDefault);
        nonDefault.forEach((setting, index) => {
            const labels = getWeatherConditionFieldLabels(setting, weatherConditionsData);
            groups.push({
                title: `Rule ${index + 1}`,
                rows: [
                    {
                        markupId: setting.markupId,
                        name: creativeHistoryName(creatives, setting.markupId),
                        conditionLabel: labels.conditionLabel,
                        qualifierLabel: labels.qualifierLabel,
                        valueLabel: labels.valueLabel,
                        timeframeLabel: labels.timeframeLabel,
                    },
                ],
            });
        });
        defaults.forEach(setting => {
            groups.push({
                title: 'Default Rule',
                rows: [
                    {
                        markupId: setting.markupId,
                        name: creativeHistoryName(creatives, setting.markupId),
                    },
                ],
            });
        });
        return groups;
    }

    const scheduled = rotationRules?.scheduled ?? [];
    if (!scheduled.length) {
        const ids = Array.isArray(snapshot?.creative) ? snapshot.creative : [];
        groups.push({
            rows: ids.map(markupId => ({
                markupId,
                name: creativeHistoryName(creatives, markupId),
            })),
        });
        return groups;
    }

    scheduled.forEach(schedule => {
        groups.push(scheduleClipboardGroup(schedule, creatives, timezone, rotationRules));
    });
    return groups;
}
